import { Iconify } from '@sohanemon/utils/components';
import { formatBytes } from '@sohanscript/lib/utils/format';
import { Button } from '@sohanscript/ui/button';
import { buttonVariants } from '@sohanscript/ui/button-variants';
import { Chip } from '@sohanscript/ui/chip';
import { FilterPills } from '@sohanscript/ui/filter-pills';
import { ImageInspector } from '@sohanscript/ui/image-inspector';
import { Input } from '@sohanscript/ui/input';
import { List, ListItem } from '@sohanscript/ui/list';
import {
	Tabs,
	TabsContent,
	TabsIndicator,
	TabsList,
	TabsTrigger,
} from '@sohanscript/ui/tabs';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { api } from '@/lib/trpc/client';
import type { RouterOutputs } from '@/server/root';
import { AssetThumbnail } from './-components/asset-thumbnail';

export const Route = createFileRoute('/admin/assets')({
	component: AdminAssetsPage,
	head: () => ({
		meta: [{ title: 'Assets - SohanScript' }],
	}),
});

const PER_PAGE = 30;

type AssetItem = RouterOutputs['asset']['list']['items'][number];
type AssetKind = 'all' | 'image' | 'file';

const KIND_OPTIONS = [
	{ value: 'all', label: 'all' },
	{ value: 'image', label: 'images' },
	{ value: 'file', label: 'files' },
] satisfies { value: AssetKind; label: string }[];

const BRAND_ASSETS = [
	{ title: 'Logo', path: '/logo.svg', format: 'SVG' },
	{ title: 'Logo bitmap', path: '/logo.png', format: 'PNG' },
	{ title: 'Avatar', path: '/avatar.png', format: 'PNG' },
	{ title: 'Open Graph image', path: '/og.png', format: 'PNG' },
	{ title: 'Favicon', path: '/favicon.ico', format: 'ICO' },
	{ title: 'Wordmark', path: '/wordmark.txt', format: 'TXT' },
] as const;

const IMAGE_EXTENSIONS = new Set([
	'avif',
	'gif',
	'ico',
	'jpeg',
	'jpg',
	'png',
	'svg',
	'webp',
]);

function isImagePath(path: string) {
	const extension = path.split('?')[0]?.split('.').at(-1)?.toLowerCase();
	return extension ? IMAGE_EXTENSIONS.has(extension) : false;
}

function assetFilename(asset: AssetItem) {
	return asset.originalFilename ?? asset._id;
}

function downloadHref(asset: AssetItem) {
	const separator = asset.url.includes('?') ? '&' : '?';
	return `${asset.url}${separator}dl=${encodeURIComponent(assetFilename(asset))}`;
}

function thumbnailSrc(asset: AssetItem) {
	const separator = asset.url.includes('?') ? '&' : '?';
	return `${asset.url}${separator}w=96&h=96&fit=crop&auto=format`;
}

type AssetRowProps = {
	url: string;
	thumbnailUrl: string | null;
	name: string;
	format: string;
	meta: string | null;
	isImage: boolean;
	filename?: string;
	size?: number | null;
	mimeType?: string | null;
	width?: number | null;
	height?: number | null;
	downloadHref: string;
	downloadName?: string;
};

function AssetRow({
	url,
	thumbnailUrl,
	name,
	filename,
	format,
	meta,
	isImage,
	size,
	mimeType,
	width,
	height,
	downloadHref,
	downloadName,
}: AssetRowProps) {
	const preview = {
		src: url,
		filename: filename ?? name,
		size,
		mimeType,
		width,
		height,
		'aria-label': `Inspect ${name}`,
		title: 'Inspect image',
	};

	return (
		<ListItem as="li" className="flex items-center gap-3 px-1 py-3">
			<span className="grid size-10 shrink-0 place-items-center overflow-hidden rounded-md border border-border/40 bg-muted/30">
				{isImage && thumbnailUrl ? (
					<AssetThumbnail src={thumbnailUrl} />
				) : (
					<Iconify
						icon="lucide:file"
						className="size-4 text-muted-foreground"
						aria-hidden="true"
					/>
				)}
			</span>
			<div className="min-w-0 flex-1">
				<div className="flex flex-wrap items-center gap-2">
					<span className="truncate font-medium text-sm">{name}</span>
					<Chip className="shrink-0">{format}</Chip>
				</div>
				{meta && (
					<p className="mt-0.5 truncate font-mono text-2xs text-muted-foreground">
						{meta}
					</p>
				)}
			</div>
			{isImage && (
				<ImageInspector
					{...preview}
					className={buttonVariants({ variant: 'ghost', size: 'icon-sm' })}
				>
					<Iconify
						icon="lucide:scan-eye"
						className="size-4"
						aria-hidden="true"
					/>
				</ImageInspector>
			)}
			<a
				href={downloadHref}
				download={downloadName ?? ''}
				aria-label={`Download ${name}`}
				className={buttonVariants({
					variant: 'ghost',
					size: 'icon-sm',
					className: 'shrink-0',
				})}
			>
				<Iconify icon="lucide:download" className="size-4" aria-hidden="true" />
			</a>
		</ListItem>
	);
}

function cdnRowProps(asset: AssetItem): AssetRowProps {
	const isImage = asset._type === 'sanity.imageAsset';
	const filename = assetFilename(asset);
	const dimensions =
		asset.width && asset.height ? `${asset.width}×${asset.height}` : null;
	const meta = [formatBytes(asset.size), asset.mimeType, dimensions]
		.filter(Boolean)
		.join(' · ');

	return {
		url: asset.url,
		thumbnailUrl: isImage ? thumbnailSrc(asset) : null,
		name: filename,
		filename,
		format: isImage ? 'image' : 'file',
		meta: meta || asset._id,
		isImage,
		size: asset.size,
		mimeType: asset.mimeType,
		width: asset.width,
		height: asset.height,
		downloadHref: downloadHref(asset),
		downloadName: filename,
	};
}

function brandRowProps(asset: (typeof BRAND_ASSETS)[number]): AssetRowProps {
	const isImage = isImagePath(asset.path);

	return {
		url: asset.path,
		thumbnailUrl: isImage ? asset.path : null,
		name: asset.title,
		filename: asset.path.split('/').at(-1) ?? asset.title,
		format: asset.format,
		meta: asset.path,
		isImage,
		downloadHref: asset.path,
	};
}

function SourceTab({
	value,
	label,
	count,
}: {
	value: string;
	label: string;
	count: number;
}) {
	return (
		<TabsTrigger value={value}>
			<span
				aria-hidden="true"
				className="text-primary/35 transition-colors group-hover:text-primary/60 group-data-active:text-primary"
			>
				~/
			</span>
			{label}
			<span className="rounded-full bg-primary/10 px-1.5 py-0.5 text-2xs text-primary/60 tabular-nums tracking-normal transition-colors group-data-active:bg-primary/15 group-data-active:text-primary">
				{count.toString().padStart(2, '0')}
			</span>
		</TabsTrigger>
	);
}

function AdminAssetsPage() {
	const [search, setSearch] = useState('');
	const [query, setQuery] = useState('');
	const [kind, setKind] = useState<AssetKind>('all');
	const [page, setPage] = useState(1);

	const submitSearch = () => {
		setQuery(search.trim());
		setPage(1);
	};

	const { data, isFetching } = api.asset.list.useQuery(
		{
			page,
			perPage: PER_PAGE,
			search: query || undefined,
			kind,
		},
		{ placeholderData: (previous) => previous },
	);

	const assets = data?.items ?? [];
	const total = data?.total ?? 0;
	const totalPages = Math.max(1, Math.ceil(total / PER_PAGE));
	const isLoading = isFetching && !data;

	return (
		<div className="pb-10">
			<section className="container min-w-0">
				<header className="py-6">
					<p className="font-mono text-2xs text-muted-foreground uppercase tracking-widest">
						~/ admin / assets
					</p>
					<h1 className="mt-1 font-semibold text-2xl">Assets</h1>
					<p className="mt-1 max-w-xl text-muted-foreground text-sm leading-relaxed">
						Browse Sanity CDN files and download the brand assets used across
						the site.
					</p>
				</header>

				<Tabs defaultValue="sanity" className="mt-2">
					<TabsList aria-label="Asset sources">
						<SourceTab value="sanity" label="Sanity" count={total} />
						<SourceTab
							value="brand"
							label="Brand"
							count={BRAND_ASSETS.length}
						/>
						<TabsIndicator />
					</TabsList>

					<TabsContent value="sanity" className="pt-5">
						<div className="flex flex-col gap-3 sm:flex-row sm:items-center">
							<form
								className="flex flex-1 items-center gap-2"
								onSubmit={(event) => {
									event.preventDefault();
									submitSearch();
								}}
							>
								<div className="relative min-w-0 flex-1">
									<Iconify
										icon="lucide:search"
										className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-muted-foreground"
										aria-hidden="true"
									/>
									<Input
										type="search"
										value={search}
										onChange={(event) => setSearch(event.target.value)}
										placeholder="Search filename or asset ID…"
										aria-label="Search CDN assets"
										className="pl-8"
									/>
								</div>
								<Button type="submit" size="sm" disabled={isFetching}>
									Search
								</Button>
							</form>
							<FilterPills
								prefix="type"
								options={KIND_OPTIONS}
								value={kind}
								onChange={(next) => {
									setKind(next);
									setPage(1);
								}}
							/>
						</div>

						<div className="mt-5" aria-busy={isFetching}>
							{isLoading ? (
								<div className="flex justify-center py-16">
									<Iconify
										icon="svg-spinners:3-dots-rotate"
										className="size-6 text-primary"
									/>
								</div>
							) : assets.length > 0 ? (
								<List as="ul">
									{assets.map((asset) => (
										<AssetRow key={asset._id} {...cdnRowProps(asset)} />
									))}
								</List>
							) : (
								<div className="flex flex-col items-center gap-2 py-16 text-center">
									<Iconify
										icon="lucide:search"
										className="size-6 text-muted-foreground"
										aria-hidden="true"
									/>
									<p className="text-muted-foreground text-sm">
										No assets match the current search.
									</p>
								</div>
							)}
						</div>

						<div className="mt-5 flex items-center justify-between gap-3">
							<Button
								variant="outline"
								size="sm"
								disabled={page <= 1 || isFetching}
								onClick={() => setPage((current) => current - 1)}
							>
								Previous
							</Button>
							<span className="font-mono text-2xs text-muted-foreground tabular-nums uppercase">
								page {page} / {totalPages}
							</span>
							<Button
								variant="outline"
								size="sm"
								disabled={page >= totalPages || isFetching}
								onClick={() => setPage((current) => current + 1)}
							>
								Next
							</Button>
						</div>
					</TabsContent>

					<TabsContent value="brand" className="pt-5">
						<List as="ul">
							{BRAND_ASSETS.map((asset) => (
								<AssetRow key={asset.path} {...brandRowProps(asset)} />
							))}
						</List>
					</TabsContent>
				</Tabs>
			</section>
		</div>
	);
}
